import posthog from "posthog-js"
import type { CaptureResult, Properties } from "posthog-js"

// Capacitor serves the bundled app from https://localhost, so every event sent
// from a native build arrives with `$host: "localhost"`. PostHog's suggested
// "filter out internal and test users" rule is `$host does not contain
// localhost`, which silently dropped every native event out of insights and
// dashboards while ingestion itself stayed perfectly healthy.
//
// Rewriting the URL properties to a host that actually identifies the native
// app keeps those events visible, and keeps them separable from PWA traffic
// rather than merged into it. Use the `app_target` super property (or `$os`)
// to tell Android and iOS apart.
export const nativeAnalyticsHost = (() => {
  try {
    return `native.${new URL(import.meta.env.VITE_WEB_APP_URL).host}`
  } catch {
    return "native.fieldbook.app"
  }
})()

const LOCALHOST_ORIGIN = /^(https?|capacitor|ionic):\/\/localhost(:\d+)?/

const rewriteValue = (value: unknown, host: string) => {
  if (typeof value !== "string") return value
  if (value === "localhost") return host
  if (LOCALHOST_ORIGIN.test(value)) {
    return value.replace(LOCALHOST_ORIGIN, `https://${host}`)
  }
  return value
}

const rewriteProperties = (
  properties: Properties | undefined,
  host: string,
) => {
  if (!properties) return
  for (const key of Object.keys(properties)) {
    properties[key] = rewriteValue(properties[key], host)
  }
}

/**
 * Replaces the Capacitor `localhost` origin in an event's URL properties with
 * `host`. Covers `$host`, `$current_url`, `$referrer` and the `$initial_*` /
 * `$session_entry_*` variants that ride along in `$set` and `$set_once`.
 *
 * Mutates and returns the event, matching PostHog's `before_send` contract.
 */
export const rewriteNativeHost = (
  event: CaptureResult | null,
  host: string = nativeAnalyticsHost,
) => {
  if (!event?.properties) return event
  rewriteProperties(event.properties, host)
  rewriteProperties(event.properties.$set as Properties | undefined, host)
  rewriteProperties(event.properties.$set_once as Properties | undefined, host)
  return event
}

const isNative = __FIELDBOOK_TARGET__ === "capacitor"

export const initPostHog = () => {
  posthog.init(
    import.meta.env.PROD
      ? import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
      : "",
    {
      api_host:
        import.meta.env.VITE_POSTHOG_PROXY_URL || "https://us.i.posthog.com",
      defaults: "2026-01-30",
      before_send: isNative ? (event) => rewriteNativeHost(event) : undefined,
      loaded: (posthog) => {
        posthog.register({
          app_version: __BUILD_ID__,
          app_target: __FIELDBOOK_TARGET__,
        })
      },
    },
  )
}
